// src/services/socketService.ts
// Singleton service that manages the Socket.IO connection.
// Mirrors the legacy SocketIOService.js pattern.
// Event broadcasting replaces AngularJS $rootScope.$broadcast.

import io from 'socket.io-client';
import { updatePeopleOnLine, findAndUpdatePeopleOnLine } from '../lib/peopleOnlineStatus';

/** Socket instance type — derived from io() return, compatible with v2 and v4. */
export type SocketInstance = ReturnType<typeof io>;

// ─── Types ──────────────────────────────────────────────────────────────────

interface ConnectionData {
  PersonId: number;
  TenancyName: string;
  Platform: string;
  Token: string;
  Muted: boolean;
}

type SocketEventHandler<T = any> = (data: T) => void;

/**
 * Reads a 'true'/'false' localStorage flag written as `key`.
 * Falls back to the legacy `ls.<key>` entry (angular-local-storage's default
 * prefix) if the plain key was never written by the React auth flow, and
 * finally to `defaultValue` if neither exists — otherwise a missing key
 * silently blocks event delivery instead of just meaning "unknown".
 */
function readBooleanFlag(key: string, defaultValue: boolean): boolean
{
  const raw = localStorage.getItem(key) ?? localStorage.getItem(`ls.${key}`);
  return raw === null ? defaultValue : raw === 'true';
}

// All socket event names emitted by the server
export type SocketEventName =
  | 'socket:client:allConnectedUsersWeb'
  | 'socket:client:notification'
  | 'socket:client:tenantNotification'
  | 'socket:client:notificationProjectChannel'
  | 'socket:client:updateViewed'
  | 'socket:client:MilestoneChanges'
  | 'socket:client:LogOutUser'
  | 'socket:client:addChatMessage'
  | 'socket:client:editChatMessage'
  | 'socket:client:deleteChatMessage'
  | 'socket:client:addGroup'
  | 'socket:client:SaveMessage'
  | 'socket:client:IsTyping'
  | 'socket:client:UpdateNotifacionChat'
  | 'socket:client:changePassword'
  | 'socket:client:listUserOnLine'
  | 'socket:client:messagesUnread'
  | 'socket:client:updateTotalMessagesUnread'
  | 'socket:client:updateImageProfile'
  | 'socket:client:updateImageBrand'
  | 'socket:client:updateNewUsers'
  | 'socket:client:updateEnableBrand'
  | 'socket:client:updateBrandName'
  | 'socket:client:updatePTOApprover'
  | 'socket:client:NotificationAttendance'
  | 'socket:client:updateHRApprover'
  | 'socket:client:updatePTOPermissions'
  | 'socket:client:UpdateSecurityGroup'
  | 'socket:client:UpdateProjects'
  | 'socket:client:backChat'
  | 'socket:client:closeChatGroups'
  | 'socket:client:WizardUpdates'
  | 'socket:client:ShowRefreshButton'
  | 'socket:client:OrderDashboard'
  | 'socket:client:showReminder';

// ─── Service ─────────────────────────────────────────────────────────────────

class SocketService
{
  private _socket: SocketInstance | null = null;

  /** Current socket connection ID — equivalent to legacy SocketIOService.connectionId */
  connectionId: string = '';

  private _onConnectionIdChange?: (id: string) => void;
  private _listeners: Map<string, Set<SocketEventHandler>> = new Map();

  get socket(): SocketInstance | null
  {
    return this._socket;
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Connect to the Socket.IO server.
   * Equivalent to legacy socketIOService.connect(url, object).
   */
  connect(serverUrl: string, connectionData: ConnectionData): SocketInstance
  {
    if (this._socket?.connected)
    {
      return this._socket;
    }

    const url = serverUrl.startsWith('http') ? serverUrl : `http://${serverUrl}`;

    this._socket = io(url + '/usersNotificationManager', {
      transports: ['websocket'],
    });

    this._socket.on('connect', () =>
    {
      this.connectionId = this._socket!.id || '';
      this._socket!.emit('addconnection', {
        ...connectionData,
        ConnectionId: this.connectionId,
      });
      // Requests the web-only unfiltered snapshot (server responds with
      // 'socket:client:allConnectedUsersWeb') instead of the chat-only
      // 'client:allConnectedUsers' (see getAllConnectedUsersWeb vs
      // getAllConnectedUsersChat in server.js).
      this._socket!.emit('client:allConnectedUsersWeb', {});
      this._onConnectionIdChange?.(this.connectionId);
    });

    this._socket.on('disconnect', () =>
    {
      this.connectionId = '';
      this._onConnectionIdChange?.('');
    });

    this._socket.on('connect_error', (err: any) =>
    {
      console.warn('🔌 Socket connection error:', err.message);
    });

    this._registerEvents(this._socket);

    return this._socket;
  }

  disconnect(): void
  {
    this._socket?.disconnect();
    this._socket = null;
    this.connectionId = '';
  }

  /** Register a callback to be notified when connectionId changes (used by SocketContext). */
  setOnConnectionIdChange(cb: (id: string) => void): void
  {
    this._onConnectionIdChange = cb;
  }

  /**
   * Subscribe to a socket event.
   * Equivalent to $rootScope.$on("socket:client:*", ...).
   * Returns an unsubscribe function — call it in useEffect cleanup.
   *
   * @example
   * useEffect(() => {
   *   return socketService.on('socket:client:notification', (data) => { ... });
   * }, []);
   */
  on(event: SocketEventName, handler: SocketEventHandler): () => void
  {
    if (!this._listeners.has(event))
    {
      this._listeners.set(event, new Set());
    }
    this._listeners.get(event)!.add(handler);
    return () => this.off(event, handler);
  }

  off(event: SocketEventName, handler: SocketEventHandler): void
  {
    this._listeners.get(event)?.delete(handler);
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  private _broadcast(event: SocketEventName, data: any): void
  {
    let payload = data;

    switch (event)
    {
      case 'socket:client:notification':
        payload = data.request ?? data;
        break;
      case 'socket:client:addChatMessage': {
        const storedPersonId = localStorage.getItem('C2_personid');
        if (data?.request?.SendByID?.toString() === storedPersonId) return;
        break;
      }
      case 'socket:client:updateTotalMessagesUnread': {
        const enableChat = readBooleanFlag('C2_enableChat', true);
        const enableInternalChat = readBooleanFlag('C2_enableInternalChat', false);
        if (!enableChat || enableInternalChat) return;
        break;
      }
      case 'socket:client:allConnectedUsersWeb':
        // Full snapshot of connected person IDs — feeds the search-results presence overlay.
        updatePeopleOnLine(data);
        break;
      case 'socket:client:listUserOnLine':
        // Single person's status change — same overlay, incremental update.
        findAndUpdatePeopleOnLine(data);
        break;
    }

    this._listeners.get(event)?.forEach(handler =>
    {
      try
      {
        handler(payload);
      }
      catch (err)
      {
        console.error(`🔌 Socket handler error for "${event}":`, err);
      }
    });
  }

  private _registerEvents(socket: SocketInstance): void
  {
    const events: SocketEventName[] = [
      'socket:client:allConnectedUsersWeb',
      'socket:client:notification',
      'socket:client:tenantNotification',
      'socket:client:notificationProjectChannel',
      'socket:client:updateViewed',
      'socket:client:MilestoneChanges',
      'socket:client:LogOutUser',
      'socket:client:UpdateNotifacionChat',
      'socket:client:changePassword',
      'socket:client:updateImageProfile',
      'socket:client:updateImageBrand',
      'socket:client:updateNewUsers',
      'socket:client:updateEnableBrand',
      'socket:client:updateBrandName',
      'socket:client:updatePTOApprover',
      'socket:client:NotificationAttendance',
      'socket:client:updateHRApprover',
      'socket:client:updatePTOPermissions',
      'socket:client:UpdateSecurityGroup',
      'socket:client:UpdateProjects',
      'socket:client:backChat',
      'socket:client:closeChatGroups',
      'socket:client:WizardUpdates',
      'socket:client:ShowRefreshButton',
      'socket:client:OrderDashboard',
      'socket:client:showReminder',
      'socket:client:addChatMessage',
      'socket:client:editChatMessage',
      'socket:client:deleteChatMessage',
      'socket:client:addGroup',
      'socket:client:SaveMessage',
      'socket:client:IsTyping',
      'socket:client:listUserOnLine',
      'socket:client:messagesUnread',
      'socket:client:updateTotalMessagesUnread',
    ];

    events.forEach(event =>
    {
      socket.on(event, (data: any) => this._broadcast(event, data));
    });
  }
}

export const socketService = new SocketService();
